<template>
    <div class="card">
        <div class="card-body">
            <!-- Toolbar with Column Toggle and Global Search -->
            <div class="d-flex justify-content-between mb-3">
                <!-- Column Toggle Dropdown -->
                <div>
                    <MultiSelect
                        v-model="selectedColumns"
                        :options="columns"
                        optionLabel="header"
                        placeholder="Show/Hide Columns"
                        class="p-column-toggle"
                    />
                </div>

                <!-- Global Search -->
<!--                <div>-->
<!--                    <InputText-->
<!--                        placeholder="Global Search"-->
<!--                        v-model="filters['global'].value"-->
<!--                        class="w-100"-->
<!--                    />-->
<!--                </div>-->
            </div>

            <!-- Data Table -->
            <DataTable
                ref="dt"
                :value="data"
                :rows="rows"
                :paginator="true"
                :totalRecords="totalRecords"
                :lazy="true"
                :loading="loading"
                v-model:filters="filters"
                filterDisplay="menu"
                :globalFilterFields="globalFilterFields"
                :rowsPerPageOptions="[10, 25, 50]"
                :resizableColumns="true"
                columnResizeMode="fit"
                @page="onPage"
                @filter="onFilter"
                showGridlines
            >
                <template #header>
                    <div class="d-flex justify-content-between">
                        <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined @click="clearFilters" />
                    </div>
                </template>

                <!-- Columns -->
                <Column
                    v-for="col in selectedColumns"
                    :key="col.field"
                    :field="col.field"
                    :header="col.header"
                    :sortable="col.sortable !== false"
                    :filter="col.filter !== false"
                    :showFilterMenu="col.filter !== false"
                    :filterMatchModeOptions="getFilterMatchModeOptions(col)"
                >
                    <template #filter="{ filterModel }">
                        <div v-if="filterModel">
                            <component
                                :is="getFilterComponent(col)"
                                v-model="filterModel.value"
                                v-bind="getFilterProps(col)"
                            ></component>
                        </div>
                        <div v-else>
                            <!-- Optional: Provide fallback or debug info -->
                            <span>No filter available for this column.</span>
                        </div>
                    </template>

                    <!-- Body Template -->
                    <template #body="{ data }">
                        <!-- Handle special columns -->
                        <span v-if="col.field === 'actions'">
                          <!-- Actions Column -->
                          <div class="btn-group">
                            <button
                                v-for="(action, key) in data[col.field]"
                                :key="key"
                                class="btn btn-sm"
                                :class="actionClasses[key] || 'btn-secondary'"
                                :title="action.text"
                                @click="navigateTo(action.url)"
                            >
                              <i :class="action.icon"></i>
                            </button>
                          </div>
                        </span>
                        <template v-else-if="col.options && col.options.length">
                            <!-- Badge Column -->
                            <template v-for="option in col.options">
                                <span
                                    v-if="option.value === data[col.field]"
                                    :class="['badge', 'badge-' + option.badge, 'text-white']"
                                >
                                  {{ option.name }}
                                </span>
                            </template>
                        </template>
                        <span
                            v-else-if="col.object && data[col.field] && data[col.field].link"
                        >
                          <!-- Hyperlink Column -->
                          <a :href="data[col.field].link">
                            {{ data[col.field].value }}
                          </a>
                        </span>
                        <span v-else>
                          <!-- Regular Column -->
                          {{ formatData(data[col.field]) }}
                        </span>
                    </template>
                </Column>
            </DataTable>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import InputNumber from 'primevue/inputnumber';
import Datepicker from 'primevue/datepicker';
import Button from 'primevue/button';
import Select from 'primevue/select';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';

export default {
    name: 'DatatableComponent',
    components: {
        DataTable,
        Column,
        InputText,
        MultiSelect,
        InputNumber,
        Datepicker,
        Button,
        Select,
    },
    props: {
        resource: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            data: [],
            columns: [],
            selectedColumns: null,
            totalRecords: 0,
            loading: false,
            page: 0,
            rows: 10,
            filters: {},
            globalFilterFields: [],
            filtersInitialized: false,
            // Filter options
            numberFilterOptions: [
                {label: 'Greater than', value: FilterMatchMode.GREATER_THAN},
                {label: 'Less than', value: FilterMatchMode.LESS_THAN},
                {label: 'Equals', value: FilterMatchMode.EQUALS},
            ],
            dateFilterOptions: [
                {label: 'After', value: FilterMatchMode.DATE_AFTER},
                {label: 'Before', value: FilterMatchMode.DATE_BEFORE},
                {label: 'Equals', value: FilterMatchMode.DATE_IS},
            ],
            selectFilterOptions: [
                {label: 'In', value: FilterMatchMode.IN},
            ],
            textFilterOptions: [
                {label: 'Starts with', value: FilterMatchMode.STARTS_WITH},
                {label: 'Ends with', value: FilterMatchMode.ENDS_WITH},
                {label: 'Contains', value: FilterMatchMode.CONTAINS},
                {label: 'Equals', value: FilterMatchMode.EQUALS},
            ],
        };
    },
    computed: {
        actionClasses() {
            return {
                view: 'btn-primary',
                edit: 'btn-secondary',
                // Add more mappings if needed
            };
        },
    },
    watch: {
        resource() {
            this.initialize();
        },
        type() {
            this.initialize();
        },
    },
    methods: {
        initialize() {
            this.page = 0;
            this.filters = {};
            this.selectedColumns = [];
            this.fetchData();
        },
        fetchData() {
            this.loading = true;
            const endpoint = `/${this.resource}/${this.type}`;
            axios
                .get(endpoint, {
                    params: {
                        page: this.page,
                        rows: this.rows,
                        filters: this.filters,
                    },
                })
                .then((response) => {
                    this.data = response.data.data;
                    this.columns = response.data.columns;
                    this.totalRecords = response.data.totalRecords;
                    if (!this.filtersInitialized) {
                        this.initFilters();
                        this.selectedColumns = this.columns.slice();
                        this.globalFilterFields = this.columns
                            .filter((col) => col.filter !== false)
                            .map((col) => col.field);
                        this.filtersInitialized = true;
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    console.error('Error fetching data:', error);
                    this.loading = false;
                });
        },
        initFilters() {
            this.filters = {
                global: {value: null, matchMode: FilterMatchMode.CONTAINS},
            };
            this.columns.forEach((col) => {
                if (col.filter !== false && col.filterTypes) {
                    this.filters[col.field] = this.getFilterModel(col.filterTypes);
                }
            });
        },
        getFilterModel(filterTypes) {
            if (filterTypes.includes('number')) {
                return {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.EQUALS}],
                };
            } else if (filterTypes.includes('date')) {
                return {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.DATE_IS}],
                };
            } else if (filterTypes.includes('select')) {
                return {value: null, matchMode: FilterMatchMode.IN};
            }
            // Default to text filter
            return {operator: FilterOperator.AND, constraints: [{value: null, matchMode: FilterMatchMode.CONTAINS}]};
        },
        getFilterMatchModeOptions(col) {
            if(!col.filterTypes) {
                return [];
            } else if (col.filterTypes.includes('number')) {
                return this.numberFilterOptions;
            } else if (col.filterTypes.includes('date')) {
                return this.dateFilterOptions;
            } else if (col.filterTypes.includes('select')) {
                return this.selectFilterOptions;
            }
            // Default to text options
            return this.textFilterOptions;
        },
        getFilterComponent(col) {
            if(!col.filterTypes) {
                return 'InputText';
            } else if (col.filterTypes.includes('select')) {
                return 'MultiSelect';
            } else if (col.filterTypes.includes('number')) {
                return 'InputNumber';
            } else if (col.filterTypes.includes('date')) {
                return 'Datepicker';
            }
            return 'InputText';
        },
        getFilterProps(col) {
            const props = {};
            if (col.filterTypes.includes('select')) {
                props.options = col.options ? col.options.map((o) => ({label: o.name, value: o.value})) : [];
                props.optionLabel = 'label';
                props.optionValue = 'value';
                props.placeholder = `Select ${col.header}`;
                props.showClear = true;
            } else if (col.filterTypes.includes('number')) {
                props.placeholder = `Enter ${col.header}`;
            } else if (col.filterTypes.includes('date')) {
                props.dateFormat = 'yy-mm-dd';
                props.placeholder = `Select ${col.header}`;
            } else {
                props.placeholder = `Search ${col.header}`;
            }
            return props;
        },
        onFilter(event) {
            // No need to set filters here; DataTable updates filters automatically via v-model:filters
            this.page = 0; // Reset to first page when filters change
            this.fetchData();
        },
        onPage(event) {
            this.page = event.page;
            this.rows = event.rows;
            this.fetchData();
        },
        clearFilters() {
            this.initFilters();
            this.fetchData();
        },
        navigateTo(url) {
            window.location.href = url;
        },
        formatData(value) {
            if (value && typeof value === 'object' && 'value' in value) {
                console.log(value);
                return value.value;
            }
            return value !== null && value !== undefined ? value : '-';
        },
    },
    mounted() {
        this.initialize();
    },
};
</script>

<style scoped>
.d-flex {
    display: flex;
}

.justify-content-between {
    justify-content: space-between;
}

.p-column-toggle {
    min-width: 200px;
}
</style>
